/* eslint-disable @nx/enforce-module-boundaries */
import React, {
  forwardRef,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  memo,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  VStack,
  useDisclosure,
} from '@mybridge/ui';
import { ArrowDown } from '@mybridge/icons';
import { Controller, useForm } from 'react-hook-form';
import { FilterPopoverPopper } from 'v4/components/filter-keyword-popover';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { SearchContext } from 'v4/snippets/search/context';
import { useIndustry } from 'v4/lib/hooks/use-industry';
import { useJobTitles } from 'v4/lib/hooks/use-job-titles';
import { FixedSizeList as List } from 'react-window';

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

const VirtualizedCheckboxList = memo(({ items, value, onChange, labelKey = 'name' }) => (
  <CheckboxGroup value={value} onChange={onChange}>
    <List
      height={190}
      itemCount={items.length}
      itemSize={32}
      width="100%"
      style={{ overflowX: 'hidden' }}
    >
      {({ index, style }) => {
        const item = items[index];
        return (
          <div style={style}>
            <Checkbox value={item[labelKey]} fontSize="16px">
              {item[labelKey]}
            </Checkbox>
          </div>
        );
      }}
    </List>
  </CheckboxGroup>
));

export const SearchJobFilterKeywords = forwardRef(
  ({ title = 'Keyword', titleKey = 'searchPage.keyword', onChange, ...props }, ref) => {
    const { t } = useTranslator();
    const { handleSubmit, control, reset } = useForm();
    const { refetchJobsSearch, setJobsFilters } = useContext(SearchContext);
    const [industries, setIndustries] = useState([]);
    const [industrySearch, setIndustrySearch] = useState('');
    const [jobFunctions, setJobFunctions] = useState([]);
    const [jobFunctionSearch, setJobFunctionSearch] = useState('');
    const [under10Applicants, setUnder10Applicants] = useState(false);
    const [benefits, setBenefits] = useState([]);
    const { data: industryData } = useIndustry({ search: '', enabled: true });
    const { data: jobFunctionData } = useJobTitles({ search: '', enabled: true });

    // Debounced values
    const debouncedIndustrySearch = useDebounce(industrySearch, 300);
    const debouncedJobFunctionSearch = useDebounce(jobFunctionSearch, 300);

    // Flatten results
    const allIndustries = useMemo(
      () => industryData?.pages?.flatMap((p) => p?.results || []) || [],
      [industryData]
    );
    const allJobFunctions = useMemo(
      () => jobFunctionData?.pages?.flatMap((p) => p?.results || []) || [],
      [jobFunctionData]
    );

    // Filtered lists (debounced)
    const filteredIndustries = useMemo(() => {
      const term = debouncedIndustrySearch.toLowerCase();
      return term
        ? allIndustries.filter((i) => i.name.toLowerCase().includes(term))
        : allIndustries;
    }, [allIndustries, debouncedIndustrySearch]);

    const filteredJobFunctions = useMemo(() => {
      const term = debouncedJobFunctionSearch.toLowerCase();
      return term
        ? allJobFunctions.filter((j) => j.title.toLowerCase().includes(term))
        : allJobFunctions;
    }, [allJobFunctions, debouncedJobFunctionSearch]);

    // Handler for inputs
    const handleSearchChange = useCallback((setter) => (e) => setter(e.target.value), []);

    const benefitOptions = useMemo(
      () => [
        '401(k)',
        'Paid maternity leave',
        'Disability insurance',
        'Vision insurance',
        'Student loan assistance',
        'Commuter benefits',
      ],
      []
    );

    // Form submit
    const onSubmit = useCallback(
      (form) => {
        const salaryValue = form.salary?.replace(/\D/g, '');
        const minSalary = form.salary?.includes('+') ? parseInt(salaryValue) : null;
        const maxSalary = !form.salary?.includes('+') ? parseInt(salaryValue) : null;

        const formattedFilters = {
          ...form,
          ...(industries.length && { industry: industries.join(',') }),
          ...(jobFunctions.length && { title: jobFunctions.join(',') }),
          ...(under10Applicants && { under10Applicants: true }),
          ...(benefits.length && { benefits: benefits.join(',') }),
          ...(minSalary && { min_salary: minSalary }),
          ...(maxSalary && { max_salary: maxSalary }),
        };

        const formData = {
          ...form,
          industries,
          jobFunctions,
          under10Applicants,
          benefits,
        };

        if (onChange) onChange(formData);
      
      },
      [industries, jobFunctions, under10Applicants, benefits, refetchJobsSearch, setJobsFilters, onChange]
    );

    return (
      <form style={{ width: '100%' }}>
        <FilterPopoverPopper
          placement="auto"
          trigger={
            <Button
              variant="transparent"
              fontSize="14px"
              color="brandGray.750"
              justifyContent="space-between"
              w="100%"
              px={1}
              type="button"
            >
              <HStack w="100%" justifyContent="space-between" spacing={1} overflow="hidden">
                <Text noOfLines={1} textAlign="left" flex={1} title={t(titleKey) || title}>
                  {t(titleKey) || title}
                </Text>
                <Box flexShrink={0}>
                  <ArrowDown />
                </Box>
              </HStack>
            </Button>
          }
          withForm
          jobsearch
          submitHandler={handleSubmit(onSubmit)}
        >
          <Stack p="20px" pr={["20px","50px"]} spacing="12px" width={["100%", "500px"]} maxH="50rem" overflowY="auto">
            {/* Industry */}
            <FormControl>
              <VStack align="start" spacing="12px">
                <FormLabel fontSize="16px" color="#3D5A80">
                  Industry
                </FormLabel>
                <Input
                  placeholder={t('searchPage.addIndustry') || 'Add an industry'}
                  onChange={handleSearchChange(setIndustrySearch)}
                  border="1px solid #DDD"
                  fontSize="16px"
                />
                <VirtualizedCheckboxList
                  items={filteredIndustries}
                  value={industries}
                  onChange={setIndustries}
                  labelKey="name"
                />
              </VStack>
            </FormControl>

            <Divider />

            {/* Job Function */}
            <FormControl>
              <VStack align="start" spacing="12px">
                <FormLabel fontSize="16px" color="#3D5A80">
                  Job Function
                </FormLabel>
                <Input
                  placeholder="Add a job function"
                  onChange={handleSearchChange(setJobFunctionSearch)}
                  border="1px solid #DDD"
                  fontSize="16px"
                  mb={2}
                />
                <VirtualizedCheckboxList
                  items={filteredJobFunctions}
                  value={jobFunctions}
                  onChange={setJobFunctions}
                  labelKey="title"
                />
              </VStack>
            </FormControl>

            <Divider />

            {/* Under 10 Applicants */}
            <FormControl>
              <HStack justify="space-between">
                <Text fontSize="16px" color="#5B5B5B">
                  Under 10 applicants
                </Text>
                <Switch
                  isChecked={under10Applicants}
                  onChange={(e) => setUnder10Applicants(e.target.checked)}
                />
              </HStack>
            </FormControl>

            <Divider />

            {/* Salary */}
            <FormControl>
              <VStack align="start" spacing="12px" w="full">
                <FormLabel fontSize="16px" color="#3D5A80">
                  Salary
                </FormLabel>
                <Controller
                  name="salary"
                  control={control}
                  render={({ field }) => {
                    const salaryOptions = [
                      { label: '$40,000+', value: '40000' },
                      { label: '$80,000+', value: '80000' },
                      { label: '$100,000+', value: '100000' },
                      { label: '$123,000+', value: '123000' },
                      { label: '$160,000+', value: '160000' },
                      { label: '$180,000+', value: '180000' },
                      { label: '$200,000+', value: '200000' },
                      { label: '$250,000+', value: '200000' },
                    ];

                    return (
                      <RadioGroup {...field} w="full">
                        <SimpleGrid columns={2} spacingY="10px" spacingX="40px" w="full">
                          {salaryOptions.map((opt) => (
                            <Radio key={opt.value} value={opt.value} fontSize="14px">
                              {opt.label}
                            </Radio>
                          ))}
                        </SimpleGrid>
                      </RadioGroup>
                    );
                  }}
                />
              </VStack>
            </FormControl>

            <Divider />

            {/* Benefits */}
            <FormControl>
              <VStack align="start" spacing="12px">
                <FormLabel fontSize="16px" color="#3D5A80">
                  Benefits
                </FormLabel>
                <CheckboxGroup value={benefits} onChange={setBenefits}>
                  <VStack align="start" spacing={2} color="#5B5B5B" w="full">
                    {benefitOptions.map((benefit) => (
                      <Checkbox key={benefit} value={benefit} fontSize="16px">
                        {benefit}
                      </Checkbox>
                    ))}
                  </VStack>
                </CheckboxGroup>
              </VStack>
            </FormControl>
          </Stack>
        </FilterPopoverPopper>
      </form>
    );
  }
);
